import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Calendar, Cloud, CloudRain, MapPin, Snowflake, Sun } from 'lucide-react';
import { eventPlaceholder } from '../lib/eventPlaceholder';
import SmartImage from './SmartImage';

const FC_ICON = { sunny: Sun, rainy: CloudRain, cloudy: Cloud, snowy: Snowflake };

/**
 * Public-facing event card. Used on the /events grid and (read-only, as_preview)
 * inside the AdminEvents "Preview before publishing" modal so editors see
 * exactly what the public will see before approving.
 */
export default function EventCard({ event, as_preview = false, forecast = null }) {
  const e = event || {};
  const dateLabel = e.start_date
    ? new Date(e.start_date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
    : '— date missing —';
  const endDateLabel =
    e.end_date && e.start_date && new Date(e.end_date).toDateString() !== new Date(e.start_date).toDateString()
      ? ` – ${new Date(e.end_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`
      : '';

  const inner = (
    <>
      <div className="aspect-[16/10] overflow-hidden relative">
        <SmartImage
          src={e.hero_image || eventPlaceholder(e.category)}
          fallback={eventPlaceholder(e.category)}
          alt={e.title || 'Event'}
          hoverZoom={!as_preview}
        />
        {e.nominee_badge && (!e.nominee_badge_until || new Date(e.nominee_badge_until + 'T23:59:59') >= new Date()) && (
          <span data-testid={`event-nominee-badge-${e.slug || 'new'}`} title={e.nominee_badge}
            className="absolute top-2.5 left-2.5 inline-flex items-center bg-forest text-[#FAF8F5] text-[10px] leading-none font-semibold px-2.5 py-1.5 rounded-full whitespace-nowrap">
            🏆 {e.nominee_badge}
          </span>
        )}
      </div>
      <div className="p-6">
        <div className="flex items-center gap-2 label text-heritage mb-2">
          <Calendar size={12} />
          {dateLabel}{endDateLabel}
          {forecast && forecast.temp_max != null && (() => {
            const FIcon = FC_ICON[forecast.condition] || Cloud;
            return (
              <span
                data-testid={`event-forecast-${e.slug || 'new'}`}
                title={forecast.label}
                className="normal-case tracking-normal font-sans text-[11px] text-charcoal/70 bg-stone border border-charcoal/10 px-2 py-0.5 rounded-full inline-flex items-center gap-1 ml-1"
              >
                <FIcon size={11} className="text-heritage"/> {Math.round(forecast.temp_max)}°
              </span>
            );
          })()}
        </div>
        <h3 className={`font-display text-2xl leading-tight mb-2 ${as_preview ? '' : 'group-hover:text-heritage transition-colors'}`}>
          {e.title || 'Untitled event'}
        </h3>
        <p className="text-muted text-sm line-clamp-2 mb-3">{e.summary}</p>
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted inline-flex items-center gap-1 min-w-0" data-testid={`event-card-venue-${e.slug || 'new'}`}>
            <MapPin size={14} className="shrink-0"/>
            <span className="truncate">{[e.venue, e.town].filter((v, i, a) => v && a.indexOf(v) === i).join(', ') || '—'}</span>
          </span>
          <span className="text-heritage font-semibold inline-flex items-center gap-1">Details <ArrowUpRight size={13}/></span>
        </div>
      </div>
    </>
  );

  if (as_preview) {
    return (
      <div
        data-testid={`event-card-preview-${e.slug || 'new'}`}
        className="bg-white border border-charcoal/10 rounded-3xl overflow-hidden block"
      >
        {inner}
      </div>
    );
  }

  return (
    <Link
      to={`/events/${e.slug}`}
      data-testid={`event-card-${e.slug}`}
      className="group bg-white border border-charcoal/10 rounded-3xl overflow-hidden hover:shadow-soft hover:-translate-y-1 transition-all block cursor-pointer"
    >
      {inner}
    </Link>
  );
}
